import {
  SortingConfiguration,
  SortingUploadRequest,
} from '../../../models/sortingDataConnection';
import { detectCsvEncoding } from '../csv/csv-file-encoding';

export const SORTING_MAX_UPLOAD_BYTES = 100 * 1024 * 1024;
const MAX_RECORDS = 100_000;
const MAX_RECORD_BYTES = 64 * 1024;

export class SortingFileError extends Error {
  constructor(readonly translationKey: string) {
    super(translationKey);
  }
}

export async function readSortingUpload(
  file: File,
  configuration: SortingConfiguration,
  uploadId: string,
): Promise<SortingUploadRequest> {
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (extension !== 'csv' && extension !== 'json') {
    throw invalid('fileType');
  }
  if (file.size === 0) {
    throw invalid('fileEmpty');
  }
  if (file.size > SORTING_MAX_UPLOAD_BYTES) {
    throw invalid('fileTooLarge');
  }
  let encoding: string;
  try {
    encoding = extension === 'csv' ? await detectCsvEncoding(file) : 'UTF-8';
  } catch {
    throw invalid('fileRead');
  }
  let decoder: TextDecoder;
  try {
    decoder = new TextDecoder(encoding, { fatal: true });
  } catch {
    throw invalid('unsupportedEncoding');
  }
  let content: string;
  try {
    content = decoder.decode(await file.arrayBuffer());
  } catch {
    throw invalid('fileRead');
  }
  if (!content.trim()) {
    throw invalid('fileEmpty');
  }
  if (extension === 'csv') {
    // CSV quoting, headers, duplicate keys and row widths are validated by the backend.
    return { format: 'csv', data: content, uploadId };
  }
  let records: unknown;
  try {
    records = JSON.parse(content);
  } catch {
    throw invalid('invalidJson');
  }
  if (!Array.isArray(records) || records.length === 0) {
    throw invalid('jsonArray');
  }
  if (records.length > MAX_RECORDS) {
    throw invalid('tooManyRecords');
  }
  const encoder = new TextEncoder();
  const keys = new Set<string>();
  for (const record of records) {
    if (!record || typeof record !== 'object' || Array.isArray(record)) {
      throw invalid('recordKey');
    }
    const key = record[configuration.keyField];
    // Never coerce numeric IDs: the original leading zeros cannot be recovered.
    if (
      typeof key !== 'string' ||
      !key.trim() ||
      hasControlCharacter(key) ||
      encoder.encode(key).length > 1024
    ) {
      throw invalid('recordKey');
    }
    if (keys.has(key)) {
      throw invalid('duplicateKey');
    }
    keys.add(key);
    if (encoder.encode(JSON.stringify(record)).length > MAX_RECORD_BYTES) {
      throw invalid('recordTooLarge');
    }
  }
  return { format: 'json', data: records, uploadId };
}

function hasControlCharacter(key: string): boolean {
  return Array.from(key).some((character) => {
    const code = character.charCodeAt(0);
    return code < 32 || (code >= 127 && code <= 159);
  });
}

function invalid(code: string): SortingFileError {
  return new SortingFileError(`dataconnection.sorting.errors.${code}`);
}
